// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/navigation';

import HomeHero from './HomeHero';

// === IMAGES ===
import sigiriya from '../assets/pick2/sigiriya/istockphoto-1492229422-612x612.jpg';
import udawalawa from '../assets/pick2/udawalawe/istockphoto-904632224-612x612.jpg';
import ella from '../assets/pick2/Ella/pexels-iakub-arifulin-205356454-11675420.jpg';
import mirissa from '../assets/pick2/Mirissa/pexels-ollivves-1005417.jpg';
import nuwaraeliya from '../assets/Aamy/28.jpg';
import kandy from '../assets/pick2/kandy/pexels-gihan-bandara-142954990-20836060.jpg';
import galle from '../assets/pick2/Galle/pexels-shainee-2933943.jpg';
import Yala from '../assets/pick2/yala/pexels-leefinvrede-18463828.jpg';
import anuradhapura from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import polonnaruwa from '../assets/pick2/polonnaruwa/istockphoto-510311016-612x612.jpg';

import excursionImage from '../assets/Aamy/20.jpeg';
import esalaPeraharaImage from '../assets/pick/35.jpg';
import safariImage from '../assets/pick/37.jpg';
import label1 from '../assets/pick/36.jpg';
import label2 from '../assets/pick2/yala/LK710A2062-02-E.webp';

import package1 from '../assets/pick/38.jpg';
import package2 from '../assets/pick2/Ella/pexels-iakub-arifulin-205356454-11675420.jpg';
import package3 from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import package4 from '../assets/pick2/yala/istockphoto-579436668-612x612.jpg';
import package5 from '../assets/pick2/Galle/istockphoto-472387838-612x612.jpg';
import package6 from '../assets/pick2/colombo/istockphoto-1181382649-612x612.jpg';

import aboutContactImage from '../assets/pick2/kandy/pexels-freestockpro-322437.jpg';

import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Gallery Images
import gallery1 from '../assets/Aamy/20.jpeg';
import gallery2 from '../assets/Aamy/2.jpeg';
import gallery3 from '../assets/Aamy/10.jpeg';
import gallery4 from '../assets/Aamy/WhatsApp Image 2025-11-13 at 13.41.03.jpeg';
import gallery5 from '../assets/Aamy/5.jpeg';
import gallery6 from '../assets/Aamy/18.jpeg';
import gallery7 from '../assets/Aamy/26.jpeg';
import gallery8 from '../assets/Aamy/16.jpeg';
import gallery9 from '../assets/Aamy/14.jpeg';
import gallery10 from '../assets/Aamy/19.jpeg';
import gallery11 from '../assets/Aamy/21.jpeg';
import gallery12 from '../assets/Aamy/27.jpeg';

// === MOTION VARIANTS ===
const sectionVariants = { hidden: { opacity: 0, y: 60, scale: 0.94 }, visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.9, ease: "easeOut" } } };
const textVariants = { hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7 } } };
const scaleUp = { hidden: { opacity: 0, scale: 0.88 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.8 } } };
const staggerContainer = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.18 } } };
const accordionItem = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

// === SLICK SETTINGS ===
const galleryMobileSettings = {
    dots: true, infinite: true, speed: 500, slidesToShow: 1, slidesToScroll: 1,
    autoplay: true, autoplaySpeed: 3000, arrows: false,
    appendDots: dots => (<div style={{ bottom: '-40px' }}><ul style={{ margin: '0px' }}>{dots}</ul></div>),
    customPaging: () => (<div className="w-2 h-2 rounded-full bg-white/30 mx-1 transition-all duration-300 hover:bg-[#D5A44F]" />)
};

// === TITLE COMPONENT ===
const TitleTwoBorder = ({ mainTitle, subTitle }) => (
    <div className="flex flex-col items-center text-center">
        <h2 className="font-konkhmer text-[2.5rem] uppercase md:text-4xl lg:text-5xl xl:text-6xl">
            <span style={{ background: 'linear-gradient(to right, #166880, #D5A44F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', textShadow: '0 0 8px rgba(22, 104, 128, 0.3)' }}>
                {mainTitle}
            </span>
        </h2>
        <p className="mt-2 font-inknut text-sm text-white/70 md:text-base lg:mt-3 lg:text-lg">{subTitle}</p>
        <div className="mt-3 flex w-full max-w-md gap-2">
            <div className="h-[3px] flex-1" style={{ background: '#166880', boxShadow: '0 0 6px #166880' }}></div>
            <div className="h-[3px] flex-1" style={{ background: '#D5A44F', boxShadow: '0 0 6px #D5A44F' }}></div>
        </div>
    </div>
);

const Home = () => {
    const { t } = useTranslation('home');

    const [activeIndex, setActiveIndex] = useState(null);
    const [activePackage, setActivePackage] = useState(0);
    const [isMd, setIsMd] = useState(false);

    const toggleService = (index) => setActiveIndex(activeIndex === index ? null : index);
    const nextSlide = () => setActivePackage((prev) => (prev + 1) % 6);
    const prevSlide = () => setActivePackage((prev) => (prev - 1 + 6) % 6);
    const slideDistance = isMd ? 180 : 340;

    // Responsive check for slider spacing
    useEffect(() => {
        const handleResize = () => setIsMd(window.innerWidth >= 768 && window.innerWidth <= 1024);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load Elfsight widget
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://elfsightcdn.com/platform.js';
        script.async = true;
        document.body.appendChild(script);
        return () => { if (document.body.contains(script)) document.body.removeChild(script); };
    }, []);

    // === TRANSLATED DATA ===
    const recommendations = [
        { label: t('recommendations.sigiriya.label'), name: t('recommendations.sigiriya.name'), image: sigiriya },
        { label: t('recommendations.udawalawa.label'), name: t('recommendations.udawalawa.name'), image: udawalawa },
        { label: t('recommendations.ella.label'), name: t('recommendations.ella.name'), image: ella },
        { label: t('recommendations.mirissa.label'), name: t('recommendations.mirissa.name'), image: mirissa },
        { label: t('recommendations.nuwaraeliya.label'), name: t('recommendations.nuwaraeliya.name'), image: nuwaraeliya },
        { label: t('recommendations.kandy.label'), name: t('recommendations.kandy.name'), image: kandy },
        { label: t('recommendations.galle.label'), name: t('recommendations.galle.name'), image: galle },
        { label: t('recommendations.yala.label'), name: t('recommendations.yala.name'), image: Yala },
        { label: t('recommendations.anuradhapura.label'), name: t('recommendations.anuradhapura.name'), image: anuradhapura },
        { label: t('recommendations.polonnaruwa.label'), name: t('recommendations.polonnaruwa.name'), image: polonnaruwa },
    ];

    const excursions = [
        { title: t('excursions.oneDay.title'), des: t('excursions.oneDay.des') },
        { title: t('excursions.twoDays.title'), des: t('excursions.twoDays.des') },
        { title: t('excursions.threeDays.title'), des: t('excursions.threeDays.des') },
        { title: t('excursions.fourDays.title'), des: t('excursions.fourDays.des') },
        { title: t('excursions.fiveDays.title'), des: t('excursions.fiveDays.des') },
        { title: t('excursions.sixDays.title'), des: t('excursions.sixDays.des') },
    ];

    const packages = [
        { id: 1, image: package1, title: t('packages.one.title'), duration: t('packages.one.duration'), description: t('packages.one.desc') },
        { id: 2, image: package2, title: t('packages.two.title'), duration: t('packages.two.duration'), description: t('packages.two.desc') },
        { id: 3, image: package3, title: t('packages.three.title'), duration: t('packages.three.duration'), description: t('packages.three.desc') },
        { id: 4, image: package4, title: t('packages.four.title'), duration: t('packages.four.duration'), description: t('packages.four.desc') },
        { id: 5, image: package5, title: t('packages.five.title'), duration: t('packages.five.duration'), description: t('packages.five.desc') },
        { id: 6, image: package6, title: t('packages.six.title'), duration: t('packages.six.duration'), description: t('packages.six.desc') },
    ];

    const featuredExperiences = [
        { h2: t('featured.perahera.h2'), h3: t('featured.perahera.h3'), h4: t('featured.perahera.h4'), paragraph: t('featured.perahera.paragraph'), image: esalaPeraharaImage, label: label1, buttonText: t('featured.perahera.button') },
        { h2: t('featured.safari.h2'), h3: t('featured.safari.h3'), paragraph: t('featured.safari.paragraph'), image: safariImage, label: label2, buttonText: t('featured.safari.button') },
    ];

    const galleryImages = [
        { src: gallery1, alt: t('gallery.alt1') }, { src: gallery2, alt: t('gallery.alt2') }, { src: gallery3, alt: t('gallery.alt3') },
        { src: gallery4, alt: t('gallery.alt4') }, { src: gallery5, alt: t('gallery.alt5') }, { src: gallery6, alt: t('gallery.alt6') },
        { src: gallery7, alt: t('gallery.alt7') }, { src: gallery8, alt: t('gallery.alt8') }, { src: gallery9, alt: t('gallery.alt9') },
        { src: gallery10, alt: t('gallery.alt10') }, { src: gallery11, alt: t('gallery.alt11') }, { src: gallery12, alt: t('gallery.alt12') },
    ];

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            {/* HERO */}
            <HomeHero />

            {/* CURATED DESTINATIONS */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="py-12">
                <div className="container mx-auto px-4">
                    <TitleTwoBorder mainTitle={t('sections.recommendations.title')} subTitle={t('sections.recommendations.subtitle')} />
                    <div className="relative mt-14 w-full lg:mt-20">
                        <Swiper modules={[Navigation, Autoplay]} spaceBetween={18} slidesPerView={1.2} loop={true} autoplay={{ delay: 4000 }} navigation={{ prevEl: '.swiper-rec-prev', nextEl: '.swiper-rec-next' }}
                            breakpoints={{ 640: { slidesPerView: 2.2 }, 768: { slidesPerView: 2.5 }, 1024: { slidesPerView: 3.2 }, 1280: { slidesPerView: 4 } }} className="pb-10">
                            {recommendations.map((rec) => (
                                <SwiperSlide key={rec.label}>
                                    <div className="group relative h-[520px] w-full overflow-hidden rounded-[18px] cursor-pointer shadow-2xl md:h-[480px] lg:h-[540px] xl:h-[680px]">
                                        <img src={rec.image} alt={rec.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-115" loading="lazy" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 transition-opacity duration-400 group-hover:opacity-100"></div>
                                        <div className="absolute bottom-8 left-0 right-0 text-center px-5">
                                            <h4 className="font-redHatDisplay text-4xl font-black text-white drop-shadow-2xl md:text-3xl lg:text-5xl xl:text-6xl">{rec.label}</h4>
                                            <h5 className="mt-2 font-redHatDisplay text-2xl font-medium text-white/90 drop-shadow-lg md:text-xl lg:text-3xl xl:text-4xl">{rec.name}</h5>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <div className="swiper-rec-prev !left-3 top-1/2 -translate-y-1/2 !text-[#166880] after:!text-4xl cursor-pointer md:!left-5 lg:after:!text-5xl"></div>
                        <div className="swiper-rec-next !right-3 top-1/2 -translate-y-1/2 !text-[#166880] after:!text-4xl cursor-pointer md:!right-5 lg:after:!text-5xl"></div>
                    </div>
                </div>
            </motion.section>

            {/* TOUR PROGRAMMES */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="">
                <div className="container mx-auto px-4 font-redHatDisplay">
                    <motion.div variants={textVariants} className="flex items-center gap-6">
                        <div className="h-px w-[50px] bg-gradient-to-r from-[#166880] to-transparent"></div>
                        <h1 className="text-[18px] font-bold uppercase md:text-[32px] lg:text-[44px]">{t('sections.excursions.title')}</h1>
                    </motion.div>
                    <motion.div variants={textVariants} className="mt-3 justify-between lg:flex">
                        <p className="w-full text-white/70 lg:w-[50%] lg:text-lg">{t('sections.excursions.desc')}</p>
                        <div className="mt-6 lg:mt-0 lg:flex lg:w-[50%] justify-end">
                            <a href="/tour" className="inline-flex items-center justify-center rounded-full bg-white px-6 py-2.5 text-lg font-bold text-black hover:bg-gray-100 transition">{t('common.viewAll')}</a>
                        </div>
                    </motion.div>
                    <motion.div variants={textVariants} className="my-8 h-px w-full bg-white/10 lg:my-14"></motion.div>
                    <div className="gap-16 lg:flex">
                        <motion.div variants={scaleUp} className="relative mb-12 h-[420px] w-full lg:mb-0 lg:h-[580px] lg:w-[50%] rounded-xl overflow-hidden shadow-2xl">
                            <img src={excursionImage} alt="Journey" className="h-full w-full object-cover" loading="lazy" />
                        </motion.div>
                        <motion.div variants={staggerContainer} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-5 lg:w-[60%]">
                            {excursions.map((service, i) => (
                                <motion.div key={i} variants={accordionItem} onClick={() => toggleService(i)} className="cursor-pointer rounded-xl border border-white/10 bg-[#111]/80 p-5 backdrop-blur-sm hover:border-[#166880] hover:bg-[#111]/90 transition-all">
                                    <h2 className="flex justify-between items-center text-lg font-bold lg:text-xl">
                                        {service.title}
                                        <span className="text-3xl font-thin text-[#D5A44F]">{activeIndex === i ? '−' : '+'}</span>
                                    </h2>
                                    {activeIndex === i && <p className="mt-3 text-sm text-white/70 lg:text-base">{service.des}</p>}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* FEATURED EXPERIENCES */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="py-20">
                <div className="container mx-auto px-4">
                    {/* Mobile */}
                    <div className="block space-y-24 sm:hidden">
                        {featuredExperiences.map((exp) => (
                            <div key={exp.h3} className="text-center">
                                <h2 className="font-redHatDisplay text-xl font-semibold uppercase text-[#166880]">{exp.h2}</h2>
                                <h3 className="font-redHatDisplay text-3xl font-bold uppercase text-white">{exp.h3}</h3>
                                {exp.h4 && <h4 className="font-redHatDisplay text-base font-medium text-[#D5A44F]">{exp.h4}</h4>}
                                <div className="relative mt-8 h-[520px] w-full rounded-[32px] border-[4px] border-white/10 overflow-hidden shadow-2xl">
                                    <img src={exp.image} alt={exp.h3} className="h-full w-full object-cover" loading="lazy" />
                                    {exp.label && <img src={exp.label} alt="" className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-[130px] w-auto" />}
                                </div>
                                <p className="mt-8 px-4 text-sm leading-7 text-white/85" dangerouslySetInnerHTML={{ __html: exp.paragraph }} />
                                <a href={exp.h3.includes("Perahera") ? "/events" : "/gallery"} className="mt-8 inline-block">
                                    <button className="rounded-full px-7 py-3.5 text-lg font-bold text-white shadow-lg" style={{ background: exp.h3.includes("Perahera") ? 'linear-gradient(to right, #166880, #0f4d5e)' : 'linear-gradient(to right, #D5A44F, #b88a3a)' }}>
                                        {exp.buttonText}
                                    </button>
                                </a>
                            </div>
                        ))}
                    </div>

                    {/* Desktop */}
                    <div className="hidden sm:block space-y-20">
                        {/* Perahera */}
                        <div className="flex flex-col md:flex-row items-center justify-between">
                            <div className="md:w-[50%] text-center md:text-left">
                                <h2 className="font-redHatDisplay text-3xl font-semibold uppercase text-[#166880]">{featuredExperiences[0].h2}</h2>
                                <h3 className="font-redHatDisplay text-5xl font-bold uppercase text-white">{featuredExperiences[0].h3}</h3>
                                <h4 className="font-redHatDisplay text-2xl font-medium text-[#D5A44F]">{featuredExperiences[0].h4}</h4>
                                <p className="mt-6 text-lg text-white/85" dangerouslySetInnerHTML={{ __html: featuredExperiences[0].paragraph }} />
                                <a href="/events"><button className="mt-8 rounded-full bg-gradient-to-r from-[#166880] to-[#0f4d5e] px-7 py-3.5 font-bold text-white hover:scale-105 transition shadow-xl">{featuredExperiences[0].buttonText}</button></a>
                            </div>
                            <div className="relative mt-10 md:mt-0 md:w-[520px] lg:w-[700px]">
                                <img src={esalaPeraharaImage} alt="Perahera" className="w-full rounded-[32px] border-[4px] border-white/10 shadow-2xl" loading="lazy" />
                                <img src={label1} alt="" className="absolute -bottom-10 left-0 hidden md:block h-[220px]" />
                            </div>
                        </div>

                        {/* Safari */}
                        <div className="flex flex-col-reverse md:flex-row items-center justify-between">
                            <div className="relative mt-10 md:mt-0 md:w-[520px] lg:w-[700px]">
                                <img src={safariImage} alt="Safari" className="w-full rounded-[32px] border-[4px] border-white/10 shadow-2xl" loading="lazy" />
                                <img src={label2} alt="" className="absolute -bottom-10 right-0 hidden md:block h-[220px]" />
                            </div>
                            <div className="md:w-[50%] text-center md:text-right">
                                <h2 className="font-redHatDisplay text-3xl font-semibold uppercase text-[#166880]">{featuredExperiences[1].h2}</h2>
                                <h3 className="font-redHatDisplay text-5xl font-bold uppercase text-white">{featuredExperiences[1].h3}</h3>
                                <p className="mt-6 text-lg text-white/85" dangerouslySetInnerHTML={{ __html: featuredExperiences[1].paragraph }} />
                                <a href="/gallery"><button className="mt-8 rounded-full bg-gradient-to-r from-[#D5A44F] to-[#b88a3a] px-7 py-3.5 font-bold text-white hover:scale-105 transition shadow-xl">{featuredExperiences[1].buttonText}</button></a>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* EXCLUSIVE PACKAGES SLIDER */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="py-12">
                <div className="container mx-auto px-4">
                    <h2 className="text-center font-konkhmer text-3xl md:text-5xl font-bold uppercase">
                        <span style={{ background: 'linear-gradient(to right, #166880, #D5A44F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                            {t('sections.packages.title')}
                        </span>
                    </h2>

                    <div className="relative flex h-[620px] md:h-[740px] lg:h-[880px] xl:h-[1020px] items-center justify-center mt-20 overflow-hidden">
                        {/* Left Arrow */}
                        <button
                            onClick={prevSlide}
                            className="absolute left-5 z-40 rounded-full border-2 border-white/30 bg-black/40 p-3.5 backdrop-blur-md hover:bg-white/20 transition-all duration-200"
                        >
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>

                        {/* Right Arrow */}
                        <button
                            onClick={nextSlide}
                            className="absolute right-5 z-40 rounded-full border-2 border-white/30 bg-black/40 p-3.5 backdrop-blur-md hover:bg-white/20 transition-all duration-200"
                        >
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 6l6 6-6 6" />
                            </svg>
                        </button>

                        {packages.map((pkg, i) => {
                            // Fix: Use correct modulo based on visible items per screen size
                            const total = packages.length; // 6
                            const normalizedIndex = ((i - activePackage + total) % total);

                            let position = '';
                            let scale = 1;
                            let opacity = 0;
                            let zIndex = 0;
                            let showDetails = false;
                            let showSideImage = false;

                            // Center (active)
                            if (normalizedIndex === 0) {
                                position = 'translateX(0)';
                                scale = 1;
                                opacity = 1;
                                zIndex = 30;
                                showDetails = true;
                            }
                            // Right side
                            else if (normalizedIndex === 1) {
                                position = `translateX(${slideDistance}px)`;
                                opacity = 0.8;
                                zIndex = 20;
                                showSideImage = true;
                            }
                            // Left side
                            else if (normalizedIndex === total - 1) {
                                position = `translateX(-${slideDistance}px)`;
                                opacity = 0.8;
                                zIndex = 20;
                                showSideImage = true;
                            }
                            // Hidden
                            else {
                                opacity = 0;
                            }

                            return (
                                <div
                                    key={pkg.id}
                                    className="absolute transition-all duration-700 ease-in-out"
                                    style={{
                                        transform: `${position} scale(${scale})`,
                                        zIndex,
                                        opacity,
                                    }}
                                >
                                    {showDetails ? (
                                        <div className="w-[270px] lg:w-[460px] xl:w-[600px]">
                                            <div className="mx-auto overflow-hidden rounded-[24px] border border-[#166880]/40 bg-gradient-to-b from-[#121212]/95 to-[#0a0a0a]/95 p-[26px] lg:p-[40px] xl:p-[54px] text-center backdrop-blur-lg shadow-2xl">
                                                <img src={pkg.image} alt={pkg.title} className="w-full h-[180px] lg:h-[390px] xl:h-[520px] rounded-[20px] object-cover" loading="lazy" />
                                                <h3 className="mt-6 font-redHatDisplay text-lg lg:text-xl xl:text-2xl font-bold uppercase">{pkg.title}</h3>
                                                <p className="text-[#D5A44F] text-lg lg:text-xl xl:text-2xl font-medium">({pkg.duration})</p>
                                                <p className="mt-4 text-xs lg:text-base xl:text-lg text-white/80">{pkg.description}</p>
                                            </div>
                                            <a href="/tour" className="block mt-6">
                                                <button className="w-full rounded-[32px] bg-gradient-to-r from-[#166880] to-[#D5A44F] py-3.5 font-bold text-white hover:scale-105 transition shadow-lg">
                                                    Reserve Now
                                                </button>
                                            </a>
                                        </div>
                                    ) : showSideImage ? (
                                        <div className="hidden md:block w-[210px] lg:w-[370px] xl:w-[490px] overflow-hidden rounded-[20px]">
                                            <img src={pkg.image} alt={pkg.title} className="w-full h-[210px] lg:h-[390px] xl:h-[520px] object-cover rounded-[20px]" loading="lazy" />
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </motion.section>

            {/* GUEST COMMENTS */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="py-4">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-konkhmer text-5xl font-bold uppercase"><span style={{ background: 'linear-gradient(to right, #166880, #D5A44F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t('sections.reviews.title')}</span></h2>
                    <p className="mt-4 text-white/70">{t('sections.reviews.subtitle')}</p>
                    <div className="mt-8 flex justify-center gap-2"><div className="h-[3px] w-32 bg-[#166880] shadow-glow"></div><div className="h-[3px] w-32 bg-[#D5A44F] shadow-glow"></div></div>
                    <div className="mt-14 flex justify-center">
                        <div className="elfsight-app-c22edb49-d10f-4305-bb46-a0fa2ba1bb9b w-full max-w-5xl" data-elfsight-app-lazy style={{ minHeight: '420px' }}></div>
                    </div>
                </div>
            </motion.section>

            {/* OUR GALLERY SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-10 bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#0a0a0a]"
            >
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="flex items-center gap-5"
                    >
                        <div className="h-px w-[40px] bg-gradient-to-r from-[#166880] to-transparent"></div>
                        <h1 className="text-[25px] font-bold uppercase font-konkhmer md:text-[30px] lg:text-[40px]">
                            <span
                                style={{
                                    background: 'linear-gradient(to right, #166880, #D5A44F)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('gallery.title')}
                            </span>
                        </h1>
                    </motion.div>

                    <motion.p
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-2 w-full font-medium text-white/70 lg:w-[50%] lg:text-lg font-inknut"
                    >
                        {t('gallery.subtitle')}
                    </motion.p>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="my-8 h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent lg:my-14"
                    />

                    {/* Mobile Carousel */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-5 block md:hidden"
                    >
                        <Slider {...galleryMobileSettings}>
                            {galleryImages.map((image, index) => (
                                <div key={index} className="px-2">
                                    <div className="relative h-[300px] w-full overflow-hidden rounded-lg">
                                        <img
                                            src={image.src}
                                            alt={image.alt}
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/300x300?text=Image+Not+Found';
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </motion.div>

                    {/* Tablet Grid */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-5 hidden md:grid md:grid-cols-2 lg:hidden gap-4"
                    >
                        {galleryImages.map((image, index) => (
                            <div key={index} className="relative h-[250px] w-full overflow-hidden rounded-lg">
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                        e.target.src = 'https://via.placeholder.com/250x250?text=Image+Not+Found';
                                    }}
                                />
                            </div>
                        ))}
                    </motion.div>

                    {/* Desktop Complex Layout */}
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-5 hidden lg:flex h-fit w-full gap-4"
                    >
                        <div className="flex w-[75%] flex-col gap-4">
                            <div className="flex h-[350px] gap-4">
                                <div className="relative h-full w-[30%] overflow-hidden rounded-lg">
                                    <img src={galleryImages[0].src} alt={galleryImages[0].alt} className="h-full w-full object-cover" />
                                </div>
                                <div className="relative h-full w-[70%] overflow-hidden rounded-lg">
                                    <img src={galleryImages[1].src} alt={galleryImages[1].alt} className="h-full w-full object-cover" />
                                </div>
                            </div>
                            <div className="flex h-[370px] gap-4">
                                <div className="relative h-full w-[60%] overflow-hidden rounded-lg">
                                    <img src={galleryImages[3].src} alt={galleryImages[3].alt} className="h-full w-full object-cover" />
                                </div>
                                <div className="relative h-full w-[40%] overflow-hidden rounded-lg">
                                    <img src={galleryImages[4].src} alt={galleryImages[4].alt} className="h-full w-full object-cover" />
                                </div>
                            </div>
                        </div>
                        <div className="flex w-[25%] flex-col gap-4">
                            <div className="relative h-[270px] w-full overflow-hidden rounded-lg">
                                <img src={galleryImages[2].src} alt={galleryImages[2].alt} className="h-full w-full object-cover" />
                            </div>
                            <div className="relative h-[450px] w-full overflow-hidden rounded-lg">
                                <img src={galleryImages[5].src} alt={galleryImages[5].alt} className="h-full w-full object-cover" />
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={textVariants}
                        className="mt-5 hidden lg:flex w-full flex-col gap-4"
                    >
                        <div className="flex h-[350px] gap-4">
                            <div className="relative h-full w-[25%] overflow-hidden rounded-lg">
                                <img src={galleryImages[6].src} alt={galleryImages[6].alt} className="h-full w-full object-cover" />
                            </div>
                            <div className="relative h-full w-[25%] overflow-hidden rounded-lg">
                                <img src={galleryImages[7].src} alt={galleryImages[7].alt} className="h-full w-full object-cover" />
                            </div>
                            <div className="relative h-full w-[50%] overflow-hidden rounded-lg">
                                <img src={galleryImages[8].src} alt={galleryImages[8].alt} className="h-full w-full object-cover" />
                            </div>
                        </div>
                        <div className="flex h-[350px] gap-4">
                            <div className="relative h-full w-[33.33%] overflow-hidden rounded-lg">
                                <img src={galleryImages[9].src} alt={galleryImages[9].alt} className="h-full w-full object-cover" />
                            </div>
                            <div className="relative h-full w-[33.33%] overflow-hidden rounded-lg">
                                <img src={galleryImages[10].src} alt={galleryImages[10].alt} className="h-full w-full object-cover" />
                            </div>
                            <div className="relative h-full w-[33.33%] overflow-hidden rounded-lg">
                                <img src={galleryImages[11].src} alt={galleryImages[11].alt} className="h-full w-full object-cover" />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </motion.section>

            {/* FINAL CTA */}
            <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants} className="py-20">
                <div className="relative h-[65vh] md:h-[75vh] flex items-center justify-center overflow-hidden">
                    <img src={aboutContactImage} alt={t('cta.alt')} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
                    <div className="relative z-10 text-center px-5 max-w-6xl">
                        <h1 className="font-konkhmer text-4xl md:text-6xl font-bold text-white">{t('cta.title')}</h1>
                        <p className="mt-6 text-lg text-white/90 max-w-3xl mx-auto">{t('cta.desc')}</p>
                        <a href="/contact-us" className="mt-10 inline-block">
                            <button className="rounded-full bg-gradient-to-r from-[#166880] to-[#0f4d5e] px-10 py-5 text-xl font-bold text-white hover:scale-105 transition shadow-2xl">
                                {t('cta.button')}
                            </button>
                        </a>
                    </div>
                </div>
            </motion.section>
        </div >
    );
};

export default Home;